import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAuth, signInAnonymously, signInWithEmailAndPassword, signOut, type Auth, type User } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, getFirestore, limit, query, where, type Firestore } from 'firebase/firestore'
import {
  ensureSupportTicketsLane,
  staffDocsKeyedByEmail,
  supportStaffCollection,
} from './utils/supportTicketsCollection'
import { getStorage, type FirebaseStorage } from 'firebase/storage'
import { getChatSupportFirebaseConfig } from './config/chatSupportEnv'

/**
 * Driver Support Chat — same Firebase project as the Flutter driver app.
 * Configure via VITE_CHAT_SUPPORT_* (.env locally, or container env → support-chat-config.js on QA/prod).
 */

export type StaffFirebaseRole = 'finance' | 'workshop' | 'admin' | 'manager' | 'team_manager'

export function parseStaffRole(raw: unknown): StaffFirebaseRole {
  const r = String(raw || 'admin').toLowerCase()
  if (r === 'finance' || r === 'workshop' || r === 'admin' || r === 'manager' || r === 'team_manager') return r
  return 'admin'
}

export interface StaffSession {
  staffUid: string
  staffName: string
  staffRole: StaffFirebaseRole
  /** true when using Flutter demo id without a real staff_users doc */
  isDemo: boolean
  /** Neo user id+email this Firebase session is bound to */
  consoleKey?: string
}

let app: FirebaseApp
let auth: Auth
let db: Firestore
let storage: FirebaseStorage
let staffSession: StaffSession | null = null

const CONSOLE_KEY_STORAGE = 'valt_staff_console_key'

function consoleIdentityKey(): string {
  try {
    const raw = localStorage.getItem('userData') || '{}'
    const u = JSON.parse(raw) as Record<string, unknown>
    const id = u.id != null ? String(u.id) : ''
    const email = String(u.email || '')
      .trim()
      .toLowerCase()
    return `${id}|${email}`
  } catch {
    return '|'
  }
}

let beforeStaffSessionClear: (() => Promise<void>) | null = null

/** Runs before Firebase sign-out so dev presence can be marked offline first. */
export function onBeforeClearStaffSession(handler: () => Promise<void>) {
  beforeStaffSessionClear = handler
}

/** Drop Firebase staff identity so the next Console login does not reuse another admin's uid. */
export async function clearStaffSession(): Promise<void> {
  try {
    if (beforeStaffSessionClear) await beforeStaffSessionClear()
  } catch {
    /* still sign out */
  }
  staffSession = null
  try {
    localStorage.removeItem(CONSOLE_KEY_STORAGE)
  } catch {
    /* ignore */
  }
  try {
    const a = getFirebaseAuth()
    if (a.currentUser) await signOut(a)
  } catch {
    /* ignore */
  }
}

export function getFirebaseApp() {
  if (!app) {
    if (!getApps().length) {
      initializeApp(getChatSupportFirebaseConfig())
    }
    app = getApps()[0]!
  }
  return app
}

export function getFirebaseAuth() {
  if (!auth) auth = getAuth(getFirebaseApp())
  return auth
}

export function getDb() {
  if (!db) db = getFirestore(getFirebaseApp())
  return db
}

export function getFirebaseStorage() {
  if (!storage) storage = getStorage(getFirebaseApp())
  return storage
}

function mapNeoRoleToStaff(role: string): StaffFirebaseRole {
  const r = role.toLowerCase()
  if (r === 'finance' || r === 'finance-staff' || r === 'finance_staff') return 'finance'
  if (r === 'workshop' || r === 'workshop-staff' || r === 'workshop_staff') return 'workshop'
  if (r === 'manager' || r === 'team_manager') return r
  return 'admin'
}

function consoleUserHints() {
  try {
    const raw = localStorage.getItem('userData')
    const roleRaw = localStorage.getItem('userRole') || ''
    if (!raw) {
      return {
        email: '',
        name: 'Console Agent',
        role: mapNeoRoleToStaff(roleRaw || 'admin'),
      }
    }
    const u = JSON.parse(raw)
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.name || u.email || 'Console Agent'
    const fromUser = String(u.role || '')
    let mapped = mapNeoRoleToStaff(fromUser || roleRaw || 'admin')
    const isTeamManagerFlag =
      u.team_manage === 1 ||
      u.team_manage === true ||
      u.team_manager === 1 ||
      u.team_manager === true ||
      fromUser === 'team_manager' ||
      roleRaw === 'team_manager'
    const isManagerFlag = u.manager === 1 || u.manager === true
    const isAdminFlag = u.admin === 1 || u.admin === true || u.owner === 1 || u.owner === true || u.org_admin === 1
    if (isTeamManagerFlag && !isAdminFlag) mapped = 'team_manager'
    else if (isManagerFlag && !isAdminFlag) mapped = 'manager'
    return {
      email: String(u.email || ''),
      name,
      role: mapped,
    }
  } catch {
    return { email: '', name: 'Console Agent', role: 'admin' as StaffFirebaseRole }
  }
}

/**
 * Staff profile for this uid. The document id is the console email,
 * so the Firebase Auth uid is read from `firebaseUid`.
 */
export async function findStaffUserData(uid: string, consoleEmail?: string): Promise<Record<string, unknown> | null> {
  await ensureSupportTicketsLane()
  const colName = supportStaffCollection()
  const db = getDb()
  if (staffDocsKeyedByEmail(colName)) {
    const email = String(consoleEmail || '')
      .trim()
      .toLowerCase()
    if (email && !email.includes('/')) {
      const byEmail = await getDoc(doc(db, colName, email))
      if (byEmail.exists()) return byEmail.data() as Record<string, unknown>
    }
    const found = await getDocs(query(collection(db, colName), where('firebaseUid', '==', uid), limit(1)))
    if (!found.empty) return found.docs[0]!.data() as Record<string, unknown>
  }
  const snap = await getDoc(doc(db, colName, uid))
  if (!snap.exists()) return null
  return snap.data() as Record<string, unknown>
}

function isPlaceholderStaffName(name: string) {
  const n = name.trim().toLowerCase()
  return !n || n === 'console agent' || n === 'staff' || n === 'agent'
}

/** Keep a real Firestore name. "Staff" is the old fallback, so use the console login instead. */
function preferredStaffName(stored: string | undefined, fallback: string) {
  const fromDoc = String(stored || '').trim()
  if (fromDoc && !isPlaceholderStaffName(fromDoc)) return fromDoc
  const fromConsole = String(fallback || '').trim()
  if (fromConsole && !isPlaceholderStaffName(fromConsole)) return fromConsole
  return fromDoc || fromConsole || 'Staff'
}

async function loadStaffUserDoc(uid: string): Promise<Partial<StaffSession> | null> {
  try {
    const hints = consoleUserHints()
    const d = await findStaffUserData(uid, hints.email)
    if (!d) return null
    const staffRole = parseStaffRole(d.role)
    return {
      staffUid: uid,
      staffName: String(d.name || d.displayName || 'Staff'),
      staffRole,
      isDemo: false,
    }
  } catch {
    return null
  }
}

/**
 * Firebase Auth uid is assignedStaffId. Never use Neo user id from localStorage.
 * Do not map staff to demo_admin — that made every admin share Mine.
 */
export async function ensureStaffSession(): Promise<StaffSession> {
  const identity = consoleIdentityKey()
  if (staffSession && staffSession.consoleKey === identity) return staffSession

  const a = getFirebaseAuth()
  const hints = consoleUserHints()
  let lastKey = ''
  try {
    lastKey = localStorage.getItem(CONSOLE_KEY_STORAGE) || ''
  } catch {
    lastKey = ''
  }

  if (a.currentUser && lastKey !== identity) {
    try {
      await signOut(a)
    } catch {
      /* ignore */
    }
  }
  staffSession = null

  const envEmail = String(import.meta.env.VITE_CHAT_SUPPORT_STAFF_EMAIL || '').trim()
  const envPassword = String(import.meta.env.VITE_CHAT_SUPPORT_STAFF_PASSWORD || '').trim()
  const consoleEmail = hints.email.trim()
  const envMatchesConsole =
    !!envEmail && !!envPassword && !!consoleEmail && consoleEmail.toLowerCase() === envEmail.toLowerCase()

  let user: User | null = a.currentUser
  if (user?.email && consoleEmail && user.email.toLowerCase() !== consoleEmail.toLowerCase()) {
    try {
      await signOut(a)
    } catch {
      /* ignore */
    }
    user = null
  }

  if (!user && envMatchesConsole) {
    try {
      const cred = await signInWithEmailAndPassword(a, envEmail, envPassword)
      user = cred.user
    } catch {
      /* fall through */
    }
  }

  if (!user) {
    try {
      const cred = await signInAnonymously(a)
      user = cred.user
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: string }).code) : ''
      if (code === 'auth/configuration-not-found' || code === 'auth/operation-not-allowed') {
        throw new Error(
          'Firebase Auth not ready: open Firebase Console → Authentication → Get started, then enable Anonymous (and Email/Password). AppId is set; Auth product must be enabled.',
        )
      }
      throw err
    }
  }

  const fromDoc = user ? await loadStaffUserDoc(user.uid) : null

  if (fromDoc?.staffUid) {
    staffSession = {
      staffUid: fromDoc.staffUid,
      staffName: preferredStaffName(fromDoc.staffName, hints.name),
      staffRole:
        hints.role === 'team_manager' || hints.role === 'manager' ? hints.role : fromDoc.staffRole || hints.role,
      isDemo: false,
      consoleKey: identity,
    }
  } else {
    staffSession = {
      staffUid: user!.uid,
      staffName: hints.name,
      staffRole: hints.role,
      isDemo: false,
      consoleKey: identity,
    }
  }

  try {
    localStorage.setItem(CONSOLE_KEY_STORAGE, identity)
  } catch {
    /* ignore */
  }
  return staffSession
}

export function getStaffSession(): StaffSession | null {
  return staffSession
}

/** @deprecated prefer ensureStaffSession */
export async function ensureFirebaseSignedIn() {
  const session = await ensureStaffSession()
  return getFirebaseAuth().currentUser || ({ uid: session.staffUid } as User)
}
